import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

import DateRangePicker, {
    type DateRange,
} from '@/components/molecules/DateRangePicker/DateRangePicker';
import type { DomainDailyLiceCount, DomainWeeklyLiceCount } from '@/domain/lice';
import OverviewChartCard from '@/routes/Client/Cage/overviewCharts/OverviewChartCard';
import GridLayout from '@/routes/Client/Cage/pages/Reports/shared/GridLayout';
import { computeDateRange } from '@/utils/date';
import { parseLocale } from '@/utils/localeFormatters';

import {
    buildFishCountChartSpec,
    buildLiceCountChartSpec,
    buildLicePerFishChartSpec,
    type LiceChartMetric,
} from '../liceCharts/liceChartSpecs';

import styles from './CageLiceGrahps.module.scss';

interface CageLiceGraphsPageContentProps {
    liceCount: DomainDailyLiceCount[];
    weeklyLice: DomainWeeklyLiceCount[];
    cageFilterHandler?: (data: DateRange) => void;
    inputDateObject?: DateRange;
    isChileanLice: boolean;
    isLoading?: boolean;
    error?: unknown;
}

export default function CageLiceGraphsPageContent({
    liceCount,
    weeklyLice,
    cageFilterHandler,
    inputDateObject,
    isChileanLice,
    isLoading,
    error,
}: CageLiceGraphsPageContentProps) {
    const { t } = useTranslation();
    const locale = parseLocale(i18n.language);

    // TODO: Replace with proper date range handling when lice count page date picker is refactored
    const lastMeasurement = liceCount.findLast((x) => x.day);
    const { dateFrom, dateTo } = computeDateRange(lastMeasurement?.day, inputDateObject);

    /** Chile does not track Caligus elongatus, and names the adult female count differently. */
    const labels = {
        female: t(
            isChileanLice ? 'cageLice.common.chileanFemaleLice' : 'cageLice.common.matureFemaleLice'
        ),
        movingStage: t('cageLice.common.mobileLice'),
        caligus: t('cageLice.common.caligus'),
        liceAxis: t('cageLice.common.liceCount'),
        temperatureAxis: t('common.labels.temperature'),
        temperature: t('common.labels.temperature'),
    };

    const weeklyMetrics: LiceChartMetric[] = isChileanLice
        ? ['female', 'movingStage']
        : ['female', 'movingStage', 'caligus'];

    const shared = {
        isLoading,
        error,
        showDateRangePicker: false,
        contentBlockSpacing: 'none' as const,
    };

    return (
        <>
            <div className={styles.datePickerContainer}>
                <DateRangePicker
                    onDateChange={cageFilterHandler}
                    dateFrom={dateFrom}
                    dateTo={dateTo}
                    disabled={isLoading}
                />
            </div>
            <GridLayout contentLayout="3">
                <OverviewChartCard
                    title={t('cageLice.common.fishCount')}
                    chartName="cage-lice-fish-count"
                    csvExport={false}
                    spec={buildFishCountChartSpec({
                        days: liceCount,
                        label: t('cageLice.common.fishCount'),
                        axisLabel: t('cageLice.common.fishCount'),
                        locale,
                    })}
                    {...shared}
                />

                <OverviewChartCard
                    title={t('cageLice.graphs.licePerFishDaily')}
                    chartName="cage-lice-per-fish-daily"
                    csvExport={false}
                    spec={buildLicePerFishChartSpec({
                        days: liceCount,
                        metrics: ['female'],
                        labels,
                        locale,
                    })}
                    {...shared}
                />

                <OverviewChartCard
                    title={t('cageLice.graphs.licePerFishWeekly')}
                    chartName="cage-lice-per-fish-weekly"
                    csvExport={false}
                    spec={buildLicePerFishChartSpec({
                        days: weeklyLice,
                        metrics: weeklyMetrics,
                        weekly: true,
                        labels,
                        locale,
                    })}
                    {...shared}
                />

                <OverviewChartCard
                    title={t('cageLice.common.liceCountAndTemperature')}
                    chartName="cage-lice-count-temperature"
                    csvExport={false}
                    spec={buildLiceCountChartSpec({
                        days: liceCount,
                        metrics: weeklyMetrics,
                        labels,
                        locale,
                    })}
                    {...shared}
                />
            </GridLayout>
        </>
    );
}
